import { TermsPrivacyFooter } from '@/components';
import Landing from '@/components/Landing';
import { getNestedBoolean, getNestedString, useConfig } from '@/utils/useConfig';
import { APP_VERSION } from '@/version';
import useJwtAuth from '@auth/services/jwt/useJwtAuth';
import { setSessionRedirectUrl } from '@fuse/core/FuseAuthorization/sessionRedirectUrl';
import FuseLoading from '@fuse/core/FuseLoading';
import Link from '@fuse/core/Link';
import { CardContent, Paper } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
import JwtLoginTab from './tabs/JwtSignInTab';

function SignInPage() {
	const navigate = useNavigate();
	const { isAuthenticated } = useJwtAuth();

	const { config, loading: _loading, error: _error, refresh: _refresh, isStale: _isStale } = useConfig('auth_pages_config');

	useEffect(() => {
		const params = new URLSearchParams(globalThis.location.search);
		const next = params.get('next');

		if (!next) {
			return;
		}

		if (!next.startsWith('/')) {
			return;
		}

		setSessionRedirectUrl(next);
	}, []);

	useEffect(() => {
		if (!isAuthenticated) {
			return;
		}

		// A universal/panel login link (?flag= or ?tag=) is in the middle of
		// swapping the session over to a new user - don't navigate away out from
		// under it just because the (soon to be replaced) old session still looks
		// authenticated at this instant.
		const params = new URLSearchParams(globalThis.location.search);

		if (params.get('flag') || params.get('tag')) {
			return;
		}

		const next = globalThis.sessionStorage.getItem('fuseRedirectUrl');
		navigate(next?.startsWith('/') ? next : '/', { replace: true });
	}, [isAuthenticated, navigate]);

	if (_loading) {
		return <FuseLoading />;
	}

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />
				<title>{getNestedString(config, 'signIn.metadata.title')}</title>
				<meta
					name="description"
					content={getNestedString(config, 'signIn.metadata.description')}
				/>
				<meta
					name="keywords"
					content={getNestedString(config, 'signIn.metadata.keywords')}
				/>
			</Helmet>
			<div className="flex min-w-0 flex-1 flex-col items-center sm:flex-row sm:justify-center md:items-start md:justify-start">
				<Paper className="h-full w-full px-16 py-8 ltr:border-r-1 rtl:border-l-1 sm:h-auto sm:w-auto sm:rounded-xl sm:p-48 sm:shadow md:flex md:h-full md:w-1/2 md:items-center md:justify-end md:rounded-none md:p-64 md:shadow-none">
					<CardContent className="mx-auto w-full max-w-320 sm:mx-0 sm:w-320 space-y-32">
						<img
							className="mx-auto"
							src={getNestedString(config, 'image')}
							alt="logo"
						/>

						<h1 className="text-4xl font-extrabold leading-tight tracking-tight">
							Sign in
						</h1>

						<JwtLoginTab />

						<div className='space-y-16'>

							{getNestedBoolean(config, 'signUp.showSignUp') as boolean ? (
								<>
									<div className="mt-32 flex items-center">
										<div className="mt-px flex-auto border-t" />
										<p className="mx-8 text-gray-700 italic">
											Don't have an account?
										</p>
										<div className="mt-px flex-auto border-t" />
									</div>

									<div className="mt-2 flex font-medium">
										You can sign up {' '}
										<Link
											className="ml-4"
											to={`/sign-up?token=${getNestedString(config, 'signUp.signUpToken')}`}
										>
											here
										</Link>
									</div>
								</>
							) : (
								<>
									<div className="mt-32 flex items-center">
										<div className="mt-px flex-auto border-t" />
										<p className="mx-8 text-gray-700 italic">
											Don't have an account?
										</p>
										<div className="mt-px flex-auto border-t" />
									</div>

									<div className="mt-2 flex font-medium">

										<Link
											className="mr-4"
											to={getNestedString(config, 'signUp.signUpInfoLink') as string}
										>
											Contact us
										</Link>
										for onboarding information
									</div>
								</>
							)}

              <TermsPrivacyFooter shortName={getNestedString(config, 'shortName')} />

							<div className="mt-32 text-center text-xs text-gray-500">
								v{APP_VERSION}
							</div>

						</div>


					</CardContent>
				</Paper>
				<Landing
					title={getNestedString(config, 'landingTitle')}
					description={getNestedString(config, 'signIn.landingDescription')}
				/>
			</div>
		</>
	);
}

export default SignInPage;
